import { isEqual } from 'lodash-es';
import { subHours } from 'date-fns';
import localDB from '@/core/database/LocalDBService';
import { RemoteDBService } from '@/core/database/RemoteDBService';
import { auth } from '@/core/auth/firebase';
import { SyncProgress } from '@/features/sync/types/sync';
import { Page } from '@/features/pages/types/page.types';
import { Collection } from '@/features/collections/types/collection.types';

type ResolvedChanges<T> = {
  toAddLocally: T[];
  toAddRemotely: T[];
  toUpdateLocally: T[];
  toUpdateRemotely: T[];
  toDeleteLocally: T[];
  toDeleteRemotely: T[];
};

// Add type for sync callbacks
interface SyncCallbacks {
  onProgress?: (progress: SyncProgress) => void;
  onCollectionSyncComplete?: () => void;
  onPageBatchSyncComplete?: (batchNumber: number, totalBatches: number | null) => void;
  onComplete?: (lastSyncedAt: number, updatedPageIds?: string[]) => Promise<void>;
  onOffline?: (error?: string) => void;
  onCollectionBatchSyncComplete?: (batchNumber: number, totalBatches: number | null) => void;
}

export class SyncEngine {
  public remoteDB: RemoteDBService;
  public localDB = localDB; // Expose localDB for worker access

  constructor() {
    this.remoteDB = new RemoteDBService();
  }

  async performSync(callbacks?: SyncCallbacks) {
    if (!auth.currentUser) {
      callbacks?.onOffline?.('User must be authenticated');
      throw Error('User must be authenticated to perform sync');
    }
    // Check if we're online
    if (!this.isOnline()) {
      console.log('Device is offline, skipping remote sync');

      // Notify about offline status
      const offlineProgress: SyncProgress = {
        pagesProcessed: 0,
        totalEstimatedPages: 0,
        currentBatch: 0,
        totalBatches: null,
        status: 'offline',
        collectionsProcessed: 0,
        totalEstimatedCollections: 0,
        currentCollectionBatch: 0,
        totalCollectionBatches: null,
        latestProcessedTimestamp: null,
        pagesCompletionPercentage: 0,
        collectionsCompletionPercentage: 0,
        overallCompletionPercentage: 0,
        currentEntityBeingProcessed: 'Offline',
      };

      callbacks?.onProgress?.(offlineProgress);
      callbacks?.onOffline?.();

      // We can still load local data
      return await localDB.getLastSyncTime();
    }

    let lastSync: number | null = await localDB.getLastSyncTime();
    lastSync = lastSync ? subHours(new Date(lastSync), 2).getTime() : null;

    const localPages = await localDB.getNotesSince(null);

    // when collections errors out, make sure to set lastSync to null to allow syncing again
    // can I ignore errors and continue and should I?
    // the count takes way too long to load, can it be done with a custom query in supabase?
    if (lastSync && localPages.length > 0) {
      // do a full comparison of all changes if there's already local data
      // it makes sense to only do batch updates if loading local data for the first time
      const lastSyncedToStore = await this.syncDataSince(lastSync, callbacks);
      return lastSyncedToStore;
    }

    console.log('Never synced data, syncing data for the first time');
    // otherwise, sync data in batches
    const lastSyncedToStore = await this.syncDataInBatches(lastSync, callbacks);
    return lastSyncedToStore;
  }

  // Check if we're online
  private isOnline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine;
  }

  private async syncDataSince(lastSync: number | null, callbacks?: SyncCallbacks) {
    console.log(
      'Performing partial sync since ',
      lastSync ? new Date(lastSync).toISOString() : null
    );
    // wrap each “block” in its own async IIFE
    const collectionsTask = (async () => {
      try {
        const [remoteCols, localCols] = await Promise.all([
          this.remoteDB.getCollectionChangesSince(lastSync),
          localDB.getCollectionsSince(lastSync),
        ]);
        const resolved = this.resolveConflicts(remoteCols, localCols);
        if (Object.values(resolved).flat().length) {
          await this.applyCollectionChanges(resolved);
        }
      } catch (err) {
        console.error('Error syncing collections:', err);
      }
    })();

    const pagesTask = (async (): Promise<number | null> => {
      try {
        const localPages = await localDB.getNotesSince(lastSync);
        const remotePages = await this.remoteDB.getPageChangesSince(lastSync);

        const changes = this.resolveConflicts(remotePages, localPages);
        await this.applyPageChanges(changes);

        const toUpdate = [...changes.toUpdateLocally, ...changes.toAddLocally];
        console.log('Sync complete, notifying UI:', {
          updatedPageIds: toUpdate.map(p => p.id),
        });

        const newSync = Date.now();
        await localDB.updateLastSyncTime(newSync);
        console.log('Updated last sync time to', new Date(newSync).toISOString());

        void callbacks?.onComplete?.(
          newSync,
          toUpdate.map(p => p.id)
        );
        return newSync;
      } catch (err) {
        console.error('Error syncing pages:', err);
        return null;
      }
    })();
    const [colsRes, pagesRes] = await Promise.allSettled([collectionsTask, pagesTask]);
    let lastSynced: number | null = null;
    if (pagesRes.status === 'fulfilled') {
      lastSynced = pagesRes.value;
    } else {
      console.error('Pages sync failed:', pagesRes.reason);
    }

    // Optional: surface collection errors too
    if (colsRes.status === 'rejected') {
      console.error('Collections sync failed:', colsRes.reason);
    }

    return lastSynced;
  }

  private async syncDataInBatches(lastSync: number | null, callbacks?: SyncCallbacks) {
    // Initialize progress tracking
    const progress: SyncProgress = {
      pagesProcessed: 0,
      totalEstimatedPages: 0,
      currentBatch: 0,
      totalBatches: null,
      status: 'collections', // Initial status
      collectionsProcessed: 0,
      totalEstimatedCollections: 0,
      currentCollectionBatch: 0,
      totalCollectionBatches: null,
      latestProcessedTimestamp: null,
      pagesCompletionPercentage: 0,
      collectionsCompletionPercentage: 0,
      overallCompletionPercentage: 0,
      currentEntityBeingProcessed: 'Starting sync...',
    };

    // Call onProgress at the beginning to show initial state
    callbacks?.onProgress?.(this.updateProgressPercentages(progress));

    try {
      const ignoreUpload = true;
      await this.syncCollections(lastSync, progress, callbacks, ignoreUpload);
      await this.syncPages(lastSync, progress, callbacks, ignoreUpload);

      // Update sync time
      const lastSynced = Date.now();
      await localDB.updateLastSyncTime(lastSynced);
      console.log('Updated last sync time to ', new Date(lastSynced).toISOString());

      // Final progress update
      progress.status = 'complete';
      progress.currentEntityBeingProcessed = 'Sync complete';
      callbacks?.onProgress?.(this.updateProgressPercentages(progress));

      // Notify sync is complete
      void callbacks?.onComplete?.(lastSynced);

      return lastSynced;
    } catch (error) {
      // Check if the error is network-related
      console.error('Sync error in performSync:', error);

      // Use the latest processed timestamp as the last sync time if available
      const lastSyncedToStore = progress.latestProcessedTimestamp || lastSync;

      // If we got a network error, handle offline state
      // This will also catch the rethrown error from syncPages
      if (
        error instanceof Error &&
        (error.message.includes('network') ||
          error.message.includes('internet') ||
          error.message.includes('offline') ||
          error.message.includes('User must be authenticated') ||
          !this.isOnline())
      ) {
        const offlineProgress: SyncProgress = {
          pagesProcessed: progress.pagesProcessed, // Preserve pages processed so far
          totalEstimatedPages: progress.totalEstimatedPages,
          currentBatch: progress.currentBatch,
          totalBatches: progress.totalBatches,
          status: 'offline',
          collectionsProcessed: progress.collectionsProcessed,
          totalEstimatedCollections: progress.totalEstimatedCollections,
          currentCollectionBatch: progress.currentCollectionBatch,
          totalCollectionBatches: progress.totalCollectionBatches,
          latestProcessedTimestamp: lastSyncedToStore,
          pagesCompletionPercentage: progress.pagesCompletionPercentage,
          collectionsCompletionPercentage: progress.collectionsCompletionPercentage,
          overallCompletionPercentage: 0,
          currentEntityBeingProcessed: 'Offline - sync failed',
        };

        callbacks?.onProgress?.(offlineProgress);
        callbacks?.onOffline?.();
      }

      // If we processed any batches successfully, update the last sync time
      if (lastSyncedToStore !== lastSync && lastSyncedToStore !== null) {
        console.log(
          `Updating last sync time to processed timestamp: ${new Date(lastSyncedToStore).toISOString()}`
        );
        await localDB.updateLastSyncTime(lastSyncedToStore);
      }

      // Return the last sync time, which might have been updated based on processed batches
      return lastSyncedToStore;
    }
  }

  // Helper method to calculate percentages
  private updateProgressPercentages(progress: SyncProgress): SyncProgress {
    // Special case: if we're starting collections sync and there's no data yet,
    // default to a sensible starting value to show some progress
    if (
      progress.status === 'collections' &&
      progress.collectionsCompletionPercentage === 0 &&
      progress.collectionsProcessed === 0
    ) {
      progress.collectionsCompletionPercentage = 0; // Start at 5% to show something
    }

    // Calculate collections percentage with higher sensitivity
    progress.collectionsCompletionPercentage =
      progress.totalEstimatedCollections > 0
        ? Math.min(
            100,
            Math.round((progress.collectionsProcessed / progress.totalEstimatedCollections) * 100)
          )
        : progress.collectionsProcessed > 0
          ? Math.min(90, progress.collectionsProcessed * 20) // More aggressive estimation (20% per collection)
          : progress.status === 'collections'
            ? Math.min(50, 50) // At least show some progress when in collections phase
            : progress.status === 'pages' || progress.status === 'complete'
              ? 100 // If we're in pages or complete phase, collections must be done
              : 0;

    // If we're transitioning to pages phase, ensure collections shows as complete
    if (progress.status === 'pages' && progress.collectionsCompletionPercentage < 100) {
      progress.collectionsCompletionPercentage = 100;
    }

    // Calculate pages percentage
    progress.pagesCompletionPercentage =
      progress.totalEstimatedPages > 0
        ? Math.min(100, Math.round((progress.pagesProcessed / progress.totalEstimatedPages) * 100))
        : progress.status === 'pages' && progress.pagesProcessed > 0
          ? Math.min(90, progress.pagesProcessed * 10) // Some estimation when we don't know total
          : progress.status === 'complete'
            ? 100 // If we're complete, pages must be done
            : 0;

    // Calculate overall percentage - collections are weighted at 50% of total progress
    if (progress.status === 'collections') {
      // More weight to collections progress
      progress.overallCompletionPercentage = Math.round(
        progress.collectionsCompletionPercentage * 0.3
      );
      progress.currentEntityBeingProcessed = `Collection: ${String(progress.collectionsProcessed)}/${String(progress.totalEstimatedCollections) || '?'}`;
    } else if (progress.status === 'pages') {
      // Start from 50% when moving to pages phase
      progress.overallCompletionPercentage =
        50 + Math.round(progress.pagesCompletionPercentage * 0.7);
      progress.currentEntityBeingProcessed = `Page: ${String(progress.pagesProcessed)}/${String(progress.totalEstimatedPages) || '?'}`;
    } else if (progress.status === 'complete') {
      progress.overallCompletionPercentage = 100;
      progress.collectionsCompletionPercentage = 100;
      progress.pagesCompletionPercentage = 100;
      progress.currentEntityBeingProcessed = 'Sync complete';
    } else {
      progress.overallCompletionPercentage = 0;
      progress.currentEntityBeingProcessed = 'Offline';
    }

    return progress;
  }

  private async syncCollections(
    lastSync: number | null,
    progress: SyncProgress,
    callbacks?: SyncCallbacks,
    ignoreUpload?: boolean
  ): Promise<void> {
    // Update progress
    progress.status = 'collections';
    progress.currentEntityBeingProcessed = 'Syncing collections...';
    callbacks?.onProgress?.(this.updateProgressPercentages(progress));

    // Get all local collections that have changed since last sync
    const localItemChanges = await localDB.getCollectionsSince(lastSync);
    console.log('Retrieved', localItemChanges.length, 'local collections');

    // Process remote collections in batches
    let processedAllCollections = false;
    let currentCollectionIndex = 0;
    let totalCollectionsProcessed = 0;
    // Smaller batch size for more frequent updates
    const COLLECTION_BATCH_SIZE = 3; // Process one collection at a time

    // Track the latest timestamp from successfully processed batches
    let latestProcessedTimestamp = lastSync;

    // Add tracking for collections to the progress object
    progress.collectionsProcessed = 0;
    progress.totalEstimatedCollections = 0;
    progress.currentCollectionBatch = 0;
    progress.totalCollectionBatches = null;
    progress.latestProcessedTimestamp = latestProcessedTimestamp;

    // First get the total count of collections to sync
    try {
      const { total } = await this.remoteDB.getCollectionChangesBatch(
        lastSync,
        0,
        1 // Just get one to check if there are any and the total count
      );

      if (total !== null) {
        progress.totalEstimatedCollections = total;
        progress.totalCollectionBatches = Math.ceil(total / COLLECTION_BATCH_SIZE);

        // If there are no collections to sync, mark as complete immediately
        if (total === 0) {
          console.log('No collections to sync');
          progress.collectionsProcessed = 0;
          progress.collectionsCompletionPercentage = 100; // Set to 100% when no collections to sync
          callbacks?.onProgress?.(this.updateProgressPercentages(progress));
          callbacks?.onCollectionSyncComplete?.();
          return;
        }
      }

      // Continue with normal sync if we have collections
    } catch (error) {
      console.error('Error getting collection count:', error);
      // Continue with sync - we'll just show indeterminate progress
    }

    while (!processedAllCollections) {
      // Get a batch of remote collections
      const { batch, hasMore, total } = await this.remoteDB.getCollectionChangesBatch(
        lastSync,
        currentCollectionIndex,
        COLLECTION_BATCH_SIZE
      );

      if (progress.totalEstimatedCollections === 0 && total) {
        progress.totalEstimatedCollections = total;
        progress.totalCollectionBatches = total ? Math.ceil(total / COLLECTION_BATCH_SIZE) : null;
      }

      progress.currentCollectionBatch++;

      if (batch.length === 0) {
        processedAllCollections = true;

        // Ensure we show 100% progress when done, even if our count was off
        if (progress.collectionsCompletionPercentage < 100) {
          progress.collectionsCompletionPercentage = 100;
          callbacks?.onProgress?.(this.updateProgressPercentages(progress));
        }
        break;
      }

      console.log(
        `Processing collection batch ${String(progress.currentCollectionBatch)} with ${String(batch.length)} collections`
      );

      progress.currentEntityBeingProcessed = `Collection batch ${String(progress.currentCollectionBatch)}/${String(progress.totalCollectionBatches) || '?'}`;
      callbacks?.onProgress?.(this.updateProgressPercentages(progress));

      // Log before resolving conflicts
      console.log('Resolving collection conflicts...');
      // Resolve conflicts and apply changes for this batch
      const resolvedCollections = this.resolveConflicts(batch, localItemChanges);

      // Log before applying changes
      console.log('Applying collection changes...');
      // Update progress to show we're in the middle of processing this batch
      progress.collectionsProcessed += batch.length * 0.5; // Count as half-processed before saving
      callbacks?.onProgress?.(this.updateProgressPercentages(progress));

      await this.applyCollectionChanges({
        toAddLocally: resolvedCollections.toAddLocally,
        toUpdateLocally: resolvedCollections.toUpdateLocally,
        toDeleteLocally: ignoreUpload ? [] : resolvedCollections.toDeleteLocally,
        toAddRemotely: ignoreUpload ? [] : resolvedCollections.toAddRemotely,
        toUpdateRemotely: ignoreUpload ? [] : resolvedCollections.toUpdateRemotely,
        toDeleteRemotely: ignoreUpload ? [] : resolvedCollections.toDeleteRemotely,
      });

      totalCollectionsProcessed += batch.length;
      // Adjust to the correct count after fully processing
      progress.collectionsProcessed = totalCollectionsProcessed;

      // Update the latest processed timestamp if we have collections in this batch
      if (batch.length > 0) {
        // Find the most recent updatedAt timestamp in the batch
        const batchLatestTimestamp = Math.max(...batch.map(c => c.updatedAt));
        if (batchLatestTimestamp > (latestProcessedTimestamp || 0)) {
          latestProcessedTimestamp = batchLatestTimestamp;
          progress.latestProcessedTimestamp = latestProcessedTimestamp;
        }
      }

      // Update progress
      callbacks?.onProgress?.(this.updateProgressPercentages(progress));

      // Check if we're done
      if (!hasMore) {
        processedAllCollections = true;
      }

      // Notify after each collection batch is processed
      callbacks?.onCollectionBatchSyncComplete?.(
        progress.currentCollectionBatch,
        progress.totalCollectionBatches
      );

      currentCollectionIndex += COLLECTION_BATCH_SIZE;
    }

    console.log(`Processed a total of ${String(totalCollectionsProcessed)} remote collections`);

    // Notify that collections are synced
    callbacks?.onCollectionSyncComplete?.();
  }

  private async syncPages(
    lastSync: number | null,
    progress: SyncProgress,
    callbacks?: SyncCallbacks,
    ignoreUpload?: boolean
  ): Promise<void> {
    // Get all local pages that have changed since last sync
    progress.status = 'pages';
    progress.currentEntityBeingProcessed = 'Syncing pages...';
    callbacks?.onProgress?.(this.updateProgressPercentages(progress));

    const localPages = await localDB.getNotesSince(lastSync);
    console.log('Retrieved ', localPages.length, ' local pages');

    // Process remote pages in batches
    let processedAllPages = false;
    let currentPage = 0;
    let totalProcessed = 0;
    const PAGE_SIZE = 5; // Same as in RemoteDBService

    // Continue tracking the latest timestamp from successfully processed batches
    let latestProcessedTimestamp = progress.latestProcessedTimestamp || lastSync;

    while (!processedAllPages) {
      // Get a batch of remote pages
      const { batch, hasMore, total } = await this.remoteDB.getPageChangesBatch(
        lastSync,
        currentPage,
        PAGE_SIZE
      );

      if (progress.totalEstimatedPages === 0 && total) {
        progress.totalEstimatedPages = total;
        progress.totalBatches = total ? Math.ceil(total / PAGE_SIZE) : null;
      }

      progress.currentBatch++;

      if (batch.length === 0) {
        processedAllPages = true;
        break;
      }

      console.log(
        `Processing batch ${String(progress.currentBatch)} with ${String(batch.length)} pages`
      );

      progress.currentEntityBeingProcessed = `Page batch ${String(progress.currentBatch)}/${String(progress.totalBatches) || '?'}`;
      callbacks?.onProgress?.(this.updateProgressPercentages(progress));

      // Resolve conflicts and apply changes for this batch
      const resolvedChanges = this.resolveConflicts(batch, localPages);
      await this.applyPageChanges({
        toAddLocally: resolvedChanges.toAddLocally,
        toUpdateLocally: resolvedChanges.toUpdateLocally,
        toDeleteLocally: ignoreUpload ? [] : resolvedChanges.toDeleteLocally,
        toAddRemotely: ignoreUpload ? [] : resolvedChanges.toAddRemotely,
        toUpdateRemotely: ignoreUpload ? [] : resolvedChanges.toUpdateRemotely,
        toDeleteRemotely: ignoreUpload ? [] : resolvedChanges.toDeleteRemotely,
      });

      totalProcessed += batch.length;
      progress.pagesProcessed = totalProcessed;

      // Update the latest processed timestamp if we have pages in this batch
      if (batch.length > 0) {
        // Find the most recent updatedAt timestamp in the batch
        const batchLatestTimestamp = Math.max(...batch.map(p => p.updatedAt));
        if (batchLatestTimestamp > (latestProcessedTimestamp || 0)) {
          latestProcessedTimestamp = batchLatestTimestamp;
          progress.latestProcessedTimestamp = latestProcessedTimestamp;
        }
        {
          console.log(
            `Checkpoint: Updated last sync time to ${new Date(batchLatestTimestamp).toISOString()}`
          );
          await localDB.updateLastSyncTime(batchLatestTimestamp);
        }
      }

      // Update progress
      callbacks?.onProgress?.(this.updateProgressPercentages(progress));

      // Notify that a page batch was synced
      callbacks?.onPageBatchSyncComplete?.(progress.currentBatch, progress.totalBatches);

      // Check if we're done
      if (!hasMore) {
        processedAllPages = true;
      }

      currentPage += PAGE_SIZE;
    }
    console.log(`Processed a total of ${String(totalProcessed)} remote pages`);
  }

  private resolveConflicts<T extends { updatedAt: number; id: string; archivedAt: number }>(
    remoteChanges: T[],
    localChanges: T[]
  ): ResolvedChanges<T> {
    const resolvedChanges: ResolvedChanges<T> = {
      toAddLocally: [],
      toUpdateLocally: [],
      toDeleteLocally: [],
      toAddRemotely: [],
      toUpdateRemotely: [],
      toDeleteRemotely: [],
    };

    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    // Create maps for faster lookups
    const remoteMap = new Map(remoteChanges.map(item => [item.id, item]));
    const localMap = new Map(localChanges.map(item => [item.id, item]));

    // Process remote changes
    for (const remoteItem of remoteChanges) {
      const localItem = localMap.get(remoteItem.id);

      // Handle deletions first
      if (remoteItem.archivedAt && remoteItem.archivedAt < oneWeekAgo) {
        resolvedChanges.toDeleteLocally.push(remoteItem);
        continue;
      }

      // Skip if items are identical
      if (isEqual(remoteItem, localItem)) {
        continue;
      }

      if (!localItem) {
        // New remote item
        resolvedChanges.toAddLocally.push(remoteItem);
      } else {
        // Conflict resolution based on updatedAt timestamp
        if (remoteItem.updatedAt > localItem.updatedAt) {
          resolvedChanges.toUpdateLocally.push(remoteItem);
        }
      }
    }

    // Process local changes
    for (const localItem of localChanges) {
      const remoteItem = remoteMap.get(localItem.id);

      // Handle deletions first
      if (localItem.archivedAt && localItem.archivedAt < oneWeekAgo) {
        resolvedChanges.toDeleteRemotely.push(localItem);
        resolvedChanges.toDeleteLocally.push(localItem);
        continue;
      }

      if (isEqual(remoteItem, localItem)) {
        continue;
      }

      if (!remoteItem) {
        // New local collection
        resolvedChanges.toAddRemotely.push(localItem);
      } else {
        // Conflict resolution based on updatedAt timestamp
        if (localItem.updatedAt > remoteItem.updatedAt) {
          resolvedChanges.toUpdateRemotely.push(localItem);
        }
      }
    }

    // Deduplicate arrays to prevent any potential double-processing
    const deduplicate = (array: T[]) =>
      Array.from(new Map(array.map(item => [item.id, item])).values());

    return {
      toAddLocally: deduplicate(resolvedChanges.toAddLocally),
      toAddRemotely: deduplicate(resolvedChanges.toAddRemotely),
      toUpdateLocally: deduplicate(resolvedChanges.toUpdateLocally),
      toUpdateRemotely: deduplicate(resolvedChanges.toUpdateRemotely),
      toDeleteLocally: deduplicate(resolvedChanges.toDeleteLocally),
      toDeleteRemotely: deduplicate(resolvedChanges.toDeleteRemotely),
    };
  }

  private async applyPageChanges(resolvedChanges: ResolvedChanges<Page>) {
    const {
      toAddLocally,
      toUpdateLocally,
      toDeleteLocally,
      toAddRemotely,
      toUpdateRemotely,
      toDeleteRemotely,
    } = resolvedChanges;

    await localDB.bulkUpsertPages([...toAddLocally, ...toUpdateLocally]);
    await localDB.bulkDeletePages(toDeleteLocally);

    await this.remoteDB.bulkAddNotes(toAddRemotely);
    await this.remoteDB.bulkUpdateNotes(toUpdateRemotely);
    await this.remoteDB.bulkDeleteItems('Note', toDeleteRemotely);
  }

  private async applyCollectionChanges(resolvedChanges: ResolvedChanges<Collection>) {
    const {
      toAddLocally,
      toUpdateLocally,
      toDeleteLocally,
      toAddRemotely,
      toUpdateRemotely,
      toDeleteRemotely,
    } = resolvedChanges;

    await localDB.bulkUpsertCollections([...toAddLocally, ...toUpdateLocally]);
    await localDB.bulkDeleteCollections(toDeleteLocally);

    await this.remoteDB.bulkAddCollections(toAddRemotely);
    await this.remoteDB.bulkUpdateCollections(toUpdateRemotely);
    await this.remoteDB.bulkDeleteItems('Collection', toDeleteRemotely);
  }
}
